class SQuickAddModal extends HTMLElement {
  #ac;
  #fetchController;
  #dialog;
  #content;
  #status;
  #triggerElement;
  #productUrl = '';

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#content = this.querySelector('[data-quick-add-content]');
    this.#status = this.querySelector('[data-quick-add-status]');

    if (!this.#dialog || !this.#content) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      if (event.command === '--open-product') this.#openProduct(event.source);
    }, opts);

    this.addEventListener('change', (event) => {
      this.#handleVariantChange(event);
    }, opts);

    this.addEventListener('submit', () => {
      if (this.#dialog?.open) this.#dialog.close();
    }, opts);

    this.#dialog.addEventListener('close', () => {
      this.#fetchController?.abort();
      this.removeAttribute('aria-busy');
      this.#restoreFocus();
    }, opts);

    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.#dialog.close();
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#fetchController?.abort();
  }

  #deferredScriptsLoaded = false;

  // Components that only appear inside the fetched product (quantity selector,
  // back-in-stock form) load on first open instead of on every page view.
  // Custom elements upgrade automatically once their definition arrives.
  #loadDeferredScripts() {
    if (this.#deferredScriptsLoaded) return;
    this.#deferredScriptsLoaded = true;
    const urls = (this.dataset.deferredScripts || '').split(' ').filter(Boolean);
    for (const url of urls) import(url).catch(() => {});
  }

  async #openProduct(source) {
    if (!(source instanceof HTMLElement)) return;

    const productUrl = source.dataset.productUrl;
    if (!productUrl) return;

    this.#triggerElement = source;
    this.#productUrl = productUrl;

    // Quick view shows the whole product; quick add (products with options) shows only the buy area.
    const mode = source.dataset.quickMode === 'view' ? 'view' : 'add';
    this.dataset.mode = mode;
    const heading = this.querySelector('[data-quick-add-heading]');
    const title = mode === 'view' ? this.dataset.titleView : this.dataset.titleAdd;
    if (heading && title) heading.textContent = title;
    this.#setLoading();
    this.#showDialog();
    this.#loadDeferredScripts();

    await this.#loadProduct(productUrl);
  }

  async #handleVariantChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const picker = target.closest('[data-quick-add-variant-picker]');
    if (!picker || !this.contains(picker)) return;

    const variantId = target.dataset.variantId || target.selectedOptions?.[0]?.dataset?.variantId;
    if (!variantId || !this.#productUrl) return;

    this.#syncVariantInput(variantId);

    const url = new URL(this.#productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);

    await this.#loadProduct(url.toString(), { focusVariantId: variantId, keepContent: true });
  }

  async #loadProduct(productUrl, { focusVariantId = null, keepContent = false } = {}) {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();
    const { signal } = this.#fetchController;

    this.setAttribute('aria-busy', 'true');
    if (!keepContent) this.#setLoading();

    try {
      const response = await fetch(productUrl, { signal });
      if (!response.ok) throw new Error(`Failed to load quick add product: ${response.status}`);

      const responseText = await response.text();
      signal.throwIfAborted();

      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const quickAddSource = html.querySelector('[data-quick-add-source]');
      if (!quickAddSource) throw new Error('Quick add source was not found in product response.');

      let content = this.#prepareProductContent(quickAddSource);
      if (this.dataset.mode === 'view') content = this.#buildQuickView(html, content);
      signal.throwIfAborted();

      this.#content.replaceChildren(content);
      this.#initializePaymentButtons();
      this.#announce('');
      this.removeAttribute('aria-busy');

      if (focusVariantId) this.#focusVariant(focusVariantId);
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error('[s-quick-add-modal] Product fetch failed:', error);
      this.removeAttribute('aria-busy');
      this.#setError();
    } finally {
      if (this.#fetchController?.signal === signal) this.#fetchController = null;
    }
  }

  #prepareProductContent(source) {
    const content = source.cloneNode(true);

    content.className = 'flex flex-col gap-6';
    content.removeAttribute('data-quick-add-source');

    for (const script of content.querySelectorAll('script')) script.remove();
    content.querySelector('#inventory-status')?.remove();

    for (const picker of content.querySelectorAll('s-variant-picker')) {
      const replacement = document.createElement('div');
      for (const attribute of picker.attributes) {
        if (attribute.name === 'class') continue;
        replacement.setAttribute(attribute.name, attribute.value);
      }
      replacement.className = picker.className;
      replacement.setAttribute('data-quick-add-variant-picker', '');
      while (picker.firstChild) replacement.appendChild(picker.firstChild);
      picker.replaceWith(replacement);
    }

    return content;
  }

  /**
   * Full product pop-up: every product photo (main image + thumbnails) beside the
   * title, rating, price, options, add to cart and the description / details
   * accordions, all taken from the product page itself.
   */
  #buildQuickView(html, buyArea) {
    const productUrl = new URL(this.#productUrl, window.location.origin);
    productUrl.searchParams.delete('variant');
    const pageUrl = productUrl.pathname;

    const layout = document.createElement('div');
    layout.className = 'grid gap-6 md:grid-cols-2 md:gap-10';

    layout.append(this.#buildQuickGallery(html), this.#buildQuickInfo(html, buyArea, pageUrl));
    return layout;
  }

  #buildQuickGallery(html) {
    const gallery = document.createElement('div');
    gallery.className = 'qv-gallery flex flex-col gap-3';

    const seen = new Set();
    const images = [];
    for (const img of html.querySelectorAll('[data-gallery-slide] img')) {
      const key = (img.currentSrc || img.getAttribute('src') || '').split('?')[0].replace(/_\d+x\d*(?=\.)/, '');
      if (!key || seen.has(key)) continue;
      seen.add(key);
      const copy = img.cloneNode();
      for (const attr of ['fetchpriority', 'style', 'width', 'height']) copy.removeAttribute(attr);
      copy.setAttribute('loading', images.length ? 'lazy' : 'eager');
      copy.setAttribute('sizes', '(min-width: 768px) 480px, 90vw');
      images.push(copy);
    }

    const main = document.createElement('div');
    main.className = 'relative aspect-square overflow-hidden rounded-card bg-surface-secondary';
    if (images[0]) {
      const first = images[0].cloneNode();
      first.className = 'size-full object-contain motion-safe:transition-opacity motion-safe:duration-300';
      main.append(first);
    }
    gallery.append(main);

    if (images.length > 1) {
      const strip = document.createElement('div');
      strip.className = 'qv-strip';
      images.forEach((image, index) => {
        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = 'qv-thumb';
        thumb.setAttribute('aria-label', `${index + 1} / ${images.length}`);
        thumb.setAttribute('aria-pressed', String(index === 0));
        const small = image.cloneNode();
        small.className = 'size-full object-contain';
        small.setAttribute('sizes', '64px');
        small.setAttribute('loading', 'lazy');
        small.alt = '';
        thumb.append(small);
        thumb.addEventListener('click', () => {
          const shown = main.querySelector('img');
          if (shown) {
            for (const attr of ['src', 'srcset', 'alt']) {
              const value = image.getAttribute(attr);
              if (value === null) shown.removeAttribute(attr);
              else shown.setAttribute(attr, value);
            }
          }
          for (const other of strip.children) other.setAttribute('aria-pressed', String(other === thumb));
        });
        strip.append(thumb);
      });
      gallery.append(strip);
    }
    return gallery;
  }

  #buildQuickInfo(html, buyArea, pageUrl) {
    const info = document.createElement('div');
    info.className = 'flex min-w-0 flex-col gap-6';

    const summary = html.querySelector('[data-quick-view-summary]')?.cloneNode(true);
    if (summary) {
      summary.removeAttribute('data-quick-view-summary');
      summary.className = 'flex flex-col';
      for (const node of summary.querySelectorAll('s-share, script')) node.remove();

      const pageTitle = summary.querySelector('h1');
      if (pageTitle) {
        const title = document.createElement('h3');
        title.className = pageTitle.className;
        const link = document.createElement('a');
        link.href = pageUrl;
        link.className = 'no-underline hover:text-accent';
        link.textContent = pageTitle.textContent.trim();
        title.append(link);
        pageTitle.replaceWith(title);
      }

      for (const anchor of summary.querySelectorAll('a[href^="#"]')) {
        anchor.href = pageUrl + anchor.getAttribute('href');
      }
      info.append(summary);
    }

    info.append(buyArea);

    const details = html.querySelector('[data-quick-view-details]')?.cloneNode(true);
    if (details) {
      details.removeAttribute('data-quick-view-details');
      for (const node of details.querySelectorAll('script')) node.remove();
      info.append(details);
    }

    if (this.dataset.viewDetails) {
      const more = document.createElement('a');
      more.href = pageUrl;
      more.className = 'self-start border-b border-on-surface pb-1 text-[11px] uppercase tracking-[0.16em] text-on-surface no-underline hover:border-accent hover:text-accent';
      more.textContent = this.dataset.viewDetails;
      info.append(more);
    }
    return info;
  }

  #initializePaymentButtons() {
    if (!this.#content?.querySelector('.shopify-payment-button, [data-shopify-payment-button], shopify-accelerated-checkout')) return;

    window.Shopify?.PaymentButton?.init?.();
  }

  #showDialog() {
    if (!this.#dialog?.open) this.#dialog.showModal();
  }

  #setLoading() {
    const loadingText = this.dataset.loadingText || '';
    this.#announce(loadingText);
    this.#content?.replaceChildren(this.#messageNode(loadingText));
  }

  #setError() {
    const errorMessage = this.dataset.errorMessage || '';
    this.#announce(errorMessage);
    this.#content?.replaceChildren(this.#messageNode(errorMessage));
  }

  #messageNode(message) {
    const node = document.createElement('p');
    node.className = 'py-12 text-center text-sm text-on-surface-secondary';
    node.textContent = message;
    return node;
  }

  #announce(message) {
    if (this.#status) this.#status.textContent = message;
  }

  #syncVariantInput(variantId) {
    const form = this.#content?.querySelector('form');
    const input = form?.querySelector('input[name="id"]');
    if (input instanceof HTMLInputElement) input.value = variantId;
  }

  #focusVariant(variantId) {
    requestAnimationFrame(() => {
      for (const input of this.#content?.querySelectorAll('[data-variant-id]') || []) {
        if (input instanceof HTMLElement && input.dataset.variantId === variantId) {
          input.focus();
          return;
        }
      }
    });
  }

  #restoreFocus() {
    if (this.#triggerElement && typeof this.#triggerElement.focus === 'function') {
      this.#triggerElement.focus();
      this.#triggerElement = null;
    }
  }
}

if (!customElements.get('s-quick-add-modal')) {
  customElements.define('s-quick-add-modal', SQuickAddModal);
}
